import Tabs from '@eduzz/houston-ui/Tabs';
import { Button, Grid, Icon } from '@material-ui/core';
import { ReportProblemOutlined } from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import PermissionHide from 'components/PermissionHide';
import { functions } from 'helpers';
import type { RefObject } from 'react';
import { memo } from 'react';
import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { listCoProductions, listMyProducts, listIrregularProducts } from 'store/Orbita/Product/actions';
import AlumyWarning from 'v2/components/Shared/AlumyWarning';
import TopPage from 'v2/components/Shared/TopPage';
import ProductsTour from 'v2/components/Shared/Tour';

import CoProductions from './components/CoProductions';
import { InputProductSearch } from './components/InputProductSearch';
import Irregular from './components/Irregular';
import ProductTable from './components/ProductTable';
import QuickView from './components/QuickView';
import { TabsIndexes, tabs } from './constants';
import styles from './styles';
import { productTourSteps } from './tour';

import type { InputProductSearchType } from './components/InputProductSearch';
import type { MyContentsProps, MyContentsState, TableType } from './constants';

class MyContents extends Component<MyContentsProps, MyContentsState> {
  private pageLoad = {
    startTime: 0,
    completed: false,
  };

  private tabsRef: RefObject<HTMLButtonElement>;

  constructor(props: MyContentsProps) {
    super(props);
    this.pageLoad.startTime = performance.now();
    this.callMoreData = this.callMoreData.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleSearchEnter = this.handleSearchEnter.bind(this);
    this.onClickOptions = this.onClickOptions.bind(this);

    this.onChangeSearch = this.onChangeSearch.bind(this);

    this.onEditProduct = this.onEditProduct.bind(this);

    this.state = {
      viewType: 1,
      activeMenu: null,
      tab: TabsIndexes.ALL,
      filters: {
        moderation_status: '',
        status: '',
        order: 'created_at-desc',
      },
      activeQuickView: false,
      idProduct: null,
      searchValue: '',
      collapseExpanded: 0,
      dataProduct: null,
      dataActivatedChildren: null,
    };

    this.tabsRef = createRef();
  }

  handleTabChange(value: number) {
    const { pagination } = this.props;
    const { filters } = this.state;

    this.setState({ tab: value });

    if (value !== TabsIndexes.COPRODUCTIONS) {
      let newFilters = { status: '', moderation_status: '', order: '' };

      const moderationStatusMap = ['all', 'approved', 'pendding', 'reproved', 'coproductions'];

      newFilters.moderation_status = moderationStatusMap[value];
      newFilters.order = filters.order;

      this.setState({ filters: newFilters, searchValue: '' });

      if (pagination && pagination.per_page) {
        this.callMoreData(newFilters, { current_page: 1, per_page: pagination.per_page });
        return;
      }

      this.callMoreData(newFilters, { current_page: 1, per_page: 10 });
    }

    this.setState({ searchValue: '' });
  }

  onChangeSearch(e: React.ChangeEvent<HTMLInputElement>) {
    this.setState({ searchValue: e.target.value });
  }

  handleSearchEnter(value: string) {
    const { tab } = this.state;
    const pagination = { current_page: 1, per_page: 20, total_pages: 0, total_items: 0 };

    switch (tab) {
      case TabsIndexes.COPRODUCTIONS:
        (async () => {
          try {
            await this.props.dispatch(listCoProductions(pagination, value));
          } catch (error) {
            toast.error(error);
          }
        })();
        break;

      case TabsIndexes.IRREGULAR:
        (async () => {
          try {
            await this.props.dispatch(listIrregularProducts(pagination, value));
          } catch (error) {
            toast.error(error);
          }
        })();
        break;

      default:
        this.callMoreData({ ...this.state.filters, title: value }, { current_page: 1, per_page: this.props.pagination?.per_page || 20 });
        break;
    }
  }

  callMoreData(filters: any, pagination: any) {
    (async () => {
      try {
        await this.props.dispatch(listMyProducts(filters, pagination));
      } catch (error) {
        toast.error(error);
      }
    })();
  }

  onShowSizeChange(current: any, size: any) {
    this.callMoreData({ ...this.state.filters, title: this.state.searchValue }, { current_page: current, per_page: size });
  }

  onChangePagination(page: any) {
    this.onShowSizeChange(page, this.props.pagination.per_page);
  }

  onChangeRowsPerPage = (per_page: number) => {
    this.callMoreData({ ...this.state.filters, title: this.state.searchValue }, { ...this.props.pagination, ...{ current_page: 1, per_page } });
  };

  onChangePage = (page: number) => {
    this.callMoreData({ ...this.state.filters, title: this.state.searchValue }, { ...this.props.pagination, ...{ current_page: page } });
  };

  onClickOptions(product: any, activatedChildren: any) {
    this.setState({
      idProduct: product.id,
      activeQuickView: true,
      dataProduct: product,
      dataActivatedChildren: activatedChildren,
    });
  }

  onEditProduct() {
    if (this.state.searchValue) {
      this.handleSearchEnter(this.state.searchValue);
    } else {
      this.callMoreData(this.state.filters, this.props.pagination);
    }

    this.setState({ collapseExpanded: 0 });
  }

  onSort = (field: string, direction: 'asc' | 'desc') => {
    const newFilters = { status: '', moderation_status: '', order: '' };
    newFilters.moderation_status = this.state.filters.moderation_status;
    newFilters.status = this.state.filters.status;
    newFilters.order = `${field}-${direction}`;

    this.setState({
      filters: newFilters,
    });

    this.callMoreData(newFilters, this.props.pagination);
  };

  handlePressEnter = (e: any) => {
    if (e.key === 'Enter') {
      this.handleSearchEnter(this.state.searchValue);
    }
  };

  loadIrregularProducts = (pagination: any, title: string) => {
    (async () => {
      try {
        await this.props.dispatch(listIrregularProducts(pagination, title));
      } catch (error) {
        toast.error(error);
      }
    })();
  };

  componentDidMount() {
    let params = this.props.match.params;
    this.loadIrregularProducts({ current_page: 1, per_page: 10 }, this.state.searchValue);
    if (params && params.tab === 'approved') {
      this.setState({ tab: TabsIndexes.APPROVED });
      this.callMoreData({ moderation_status: 'approved', order: 'created_at-desc' }, { current_page: 1, per_page: 10 });
    } else if (params && params.tab === 'reproved') {
      this.setState({ tab: TabsIndexes.REPROVED });
      this.callMoreData({ moderation_status: 'reproved', order: 'created_at-desc' }, { current_page: 1, per_page: 10 });
    } else {
      this.callMoreData({ order: 'created_at-desc' }, { current_page: 1, per_page: 10 });
    }
  }

  componentDidUpdate(prevProps: MyContentsProps) {
    if ((prevProps.irregularProducts ?? []).length !== (this.props.irregularProducts ?? []).length) {
      this.setState({ tab: TabsIndexes.IRREGULAR });
    }

    if (!this.pageLoad.completed && this.props.isCompleted) {
      this.pageLoad.completed = true;
    }

    this.state.activeQuickView ? functions.removeScrollPage() : functions.addScrollPage();
  }

  render() {
    const products = this.props.products;
    const irregularProducts = this.props.irregularProducts;
    const pagination = this.props.pagination;
    const { classes } = this.props;

    return (
      <div className={classes.root} id='my-products'>
        <AlumyWarning />

        <TopPage
          title='Meus Produtos'
          action={
            <PermissionHide action='myeduzz:meus-produtos:criar'>
              <Link to={'/producer/product/newproduct'}>
                <Button id='myNewProductButton' color='secondary' variant='contained'>
                  <Icon style={{ fontSize: 20, marginRight: 8 }}>add</Icon> Criar Novo Produto
                </Button>
              </Link>
            </PermissionHide>
          }
        />

        {!!irregularProducts?.length && new Date(Date.now()) < new Date('2025-02-04 00:00:00') && (
          <div style={{ paddingBottom: 16 }}>
            <Alert severity='warning'>
              <b>Atenção: Ajuste necessário nos seus produtos no MyEduzz</b>
              <br />
              Identificamos que alguns de seus produtos estão sem entregas cadastradas devido à exclusão do curso no Nutror. Essa situação pode gerar{' '}
              <b>reembolsos</b> e <b>chargebacks</b> em caso de vendas.
              <br />
              Para evitar problemas, pedimos que você ajuste as entregas dos produtos destacados na aba <b>{'"Atenção"'}</b>.
              <br />
              <b>Prazo:</b> Ajuste até <b>03/02/2025</b>.{' '}
              <b>Após essa data, os produtos sem entregas cadastradas serão reprovados na moderação e suas vendas serão suspensas</b>.
              <br />
              Em caso de dúvidas, entre em contato com o{' '}
              <a href='https://orbita.eduzz.com/producer/help' target='_blank' rel='noreferrer'>
                atendimento através do chat
              </a>
              .
            </Alert>
          </div>
        )}

        <Grid container justify='space-between' alignItems='center' spacing={2}>
          <Grid item xs={12}>
            <Tabs value={this.state.tab} onChange={this.handleTabChange} style={{ paddingBottom: 16 }}>
              {tabs.map((t) => (
                <Tabs.Tab key={t} label={t}>
                  <TabProductContent
                    table={{
                      products,
                      order: this.state.filters.order,
                      listChildren: this.props.listProductsChildren,
                      onSort: this.onSort,
                      onClickOptions: this.onClickOptions,
                      isLoading: this.props.isFetching,
                      pagination: {
                        current_page: pagination?.current_page ?? 1,
                        per_page: pagination?.per_page ?? 10,
                        total: pagination?.total_items,
                        optionsPerPage: '10,20,30,40',
                        onChangePage: this.onChangePage,
                        onChangePerPage: this.onChangeRowsPerPage,
                      },
                    }}
                    inputSearch={{
                      onChange: this.onChangeSearch,
                      onKeyPress: this.handlePressEnter,
                      value: this.state.searchValue,
                      classes: this.props.classes,
                      handleSearchEnter: this.handleSearchEnter,
                    }}
                  />
                </Tabs.Tab>
              ))}
              <Tabs.Tab label='Co-Produções'>
                <InputProductSearch
                  classes={this.props.classes}
                  onChange={this.onChangeSearch}
                  onKeyPress={this.handlePressEnter}
                  value={this.state.searchValue}
                  handleSearchEnter={this.handlePressEnter}
                />
                <CoProductions user={this.props.currentUser} search={this.state.searchValue} />
              </Tabs.Tab>
              {!!irregularProducts?.length && new Date(Date.now()) < new Date('2025-02-04 00:00:00') && (
                <Tabs.Tab icon={<ReportProblemOutlined style={{ color: '#ff9800' }} fontSize='small' />} label='Atenção'>
                  {/* <InputProductSearch
                    classes={this.props.classes}
                    onChange={this.onChangeSearch}
                    onKeyPress={this.handlePressEnter}
                    value={this.state.searchValue}
                    handleSearchEnter={this.handlePressEnter}
                  /> */}
                  {/* Comentado pois não funciona ao atualizar, necessita melhoria */}

                  <Irregular user={this.props.currentUser} search={this.state.searchValue} />
                </Tabs.Tab>
              )}
            </Tabs>
          </Grid>
        </Grid>
        <QuickView
          active={this.state.activeQuickView}
          changeActive={() => {
            this.setState({ activeQuickView: !this.state.activeQuickView });
          }}
          onEditProduct={this.onEditProduct}
          productData={this.state.dataProduct}
          activatedChildren={this.state.dataActivatedChildren}
        />
      </div>
    );
  }
}

const TabProductContent = memo(({ table, inputSearch }: { table: TableType; inputSearch: InputProductSearchType }) => {
  return (
    <div id='myProductsList'>
      <InputProductSearch {...inputSearch} />
      <ProductsTour name='products_tour' steps={productTourSteps} />
      <ProductTable {...table} />
    </div>
  );
});

function mapStateToProps(state: any) {
  return {
    products: state.orbita.Product.listMine.data,
    pagination: state.orbita.Product.listMine.pagination,
    isFetching: state.orbita.Product.listMine.isFetching,
    childrenOpen: state.orbita.Product.listProductsChildren.data,
    listProductsChildren: state.orbita.Product.listProductsChildren,
    isCompleted: Array.isArray(state.orbita.Product.listMine.data),
    currentUser: state.orbita.User.user.user,
    irregularProducts: state.orbita.Product.listIrregularProducts.data,
  };
}
export default connect(mapStateToProps)(styles(MyContents));
